'use client';

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AmplitudeEvent, amplitude } from '@/lib/analytics/amplitude';
import { translate } from '@/lib/i18n/translate';
import OnboardingApi from '@/repositories/onboarding';
import { useTerms } from './useTerms';

/**
 * Signature of the current terms of use, as required by the modal the API
 * interceptors open when the backend answers `TERMS_OF_USE_NOT_ACCEPTED`.
 *
 * On success every query is invalidated: the call that triggered the modal was
 * rejected, so the screens behind it are showing data they failed to load.
 */
export function useTermsAcceptance() {
  const $ = _c(11);
  const queryClient = useQueryClient();
  const {
    termsOfUseUrl,
    isLoading: isLoadingUrl
  } = useTerms();
  let t0;
  if ($[0] !== termsOfUseUrl) {
    t0 = async () => {
      if (!termsOfUseUrl) {
        throw new Error("Terms of use URL is unavailable");
      }
      return await OnboardingApi.acceptTerms({
        url: termsOfUseUrl,
        label: translate("COMPONENTS.TERMS_OF_USE_MODAL.LABEL")
      });
    };
    $[0] = termsOfUseUrl;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient) {
    t1 = async () => {
      amplitude.logEvent(AmplitudeEvent.TERMS_ACCEPTED);
      await queryClient.invalidateQueries();
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    mutateAsync: accept,
    isPending
  } = useMutation(t2);
  const t3 = isPending || isLoadingUrl;
  let t4;
  if ($[7] !== accept || $[8] !== t3 || $[9] !== termsOfUseUrl) {
    t4 = {
      termsOfUseUrl,
      accept,
      isLoading: t3
    };
    $[7] = accept;
    $[8] = t3;
    $[9] = termsOfUseUrl;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}